/**
 * Booking save payload — write only what the operator changed.
 *
 * The booking edit page keeps two copies of the row: the one loaded when the
 * page opened (`original`) and the one the operator is editing (`current`).
 * Save used to send every editable column from `current`, whether or not it
 * had changed. That silently reverted anything another writer had done to the
 * row in the meantime.
 *
 * That is exactly what happened on BK-2026-007218: an operator opened the
 * booking, the customer added two add-ons and paid through the manage-booking
 * app (total 180 → 214.50, payment_state open → paid), and the operator then
 * saved an internal note. The save carried the stale subtotal, total_tax,
 * total_amount and payment_state along with the note, and the booking was
 * left showing an "overpayment" of 34.50 with the payment still open.
 *
 * Two rules fix this:
 *
 *   1. Only columns whose value differs between `original` and `current` are
 *      sent. A note edit sends the note. Columns the operator never touched
 *      keep whatever the server has now, whoever wrote it.
 *
 *   2. When the operator DID change money (added an item, changed dates,
 *      applied a discount, set a delivery price), the new totals were derived
 *      from the copy loaded at open time. If the server's money columns have
 *      moved since then, that derivation started from a stale base and would
 *      drop the other writer's change from the total. The page re-reads the
 *      row before saving and refuses the save in that case, so the operator
 *      reloads and re-applies the edit on top of the current values.
 *
 * Comparison is lenient where the two copies legitimately differ in shape:
 * null / undefined / '' are one value, numeric columns compare as numbers to
 * two decimals (PostgREST may hand back a numeric as a number or a string),
 * and date columns compare as instants (the form emits `.000Z`, the server
 * returns `+00:00`). Free-text columns compare as strings, so a phone number
 * that happens to look numeric is never treated as a number.
 */

import type { Database } from './database.types';

type Booking = Database['public']['Tables']['bookings']['Row'];
type BookingUpdate = Database['public']['Tables']['bookings']['Update'];

/** Every column the edit page is allowed to write on Save. */
export const BOOKING_SAVE_FIELDS = [
  'customer_firstname',
  'customer_lastname',
  'customer_email',
  'customer_phone',
  'start_date',
  'end_date',
  'booking_state',
  'payment_state',
  'delivery_street',
  'delivery_zip',
  'delivery_city',
  'delivery_country',
  'delivery_notes',
  'delivery_price',
  'code_gate',
  'customer_notes',
  'internal_notes',
  'notes',
  'discount_code_id',
  'discount_code_used',
  'total_discount',
  'subtotal',
  'total_tax',
  'total_amount',
  'language',
  'marketing_consent',
] as const satisfies readonly (keyof Booking)[];

export type BookingSaveField = (typeof BOOKING_SAVE_FIELDS)[number];

/**
 * Columns that describe what the booking costs and how it has been paid.
 * A local change to any of these was derived from the loaded copy; a server
 * change to any of these since load means that copy is stale.
 */
export const BOOKING_MONEY_FIELDS = [
  'subtotal',
  'total_tax',
  'total_amount',
  'total_discount',
  'delivery_price',
  'discount_code_id',
  'discount_code_used',
  'payment_state',
  'amount_paid',
] as const satisfies readonly (keyof Booking)[];

export type BookingMoneyField = (typeof BOOKING_MONEY_FIELDS)[number];

const NUMERIC_FIELDS: ReadonlySet<keyof Booking> = new Set<keyof Booking>([
  'subtotal',
  'total_tax',
  'total_amount',
  'total_discount',
  'delivery_price',
  'amount_paid',
]);

const DATE_FIELDS: ReadonlySet<keyof Booking> = new Set<keyof Booking>(['start_date', 'end_date']);

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

/** Compare one column's value across two copies of a booking. */
export function bookingFieldEquals(field: keyof Booking, a: unknown, b: unknown): boolean {
  if (isBlank(a) && isBlank(b)) return true;
  if (isBlank(a) || isBlank(b)) return false;

  if (NUMERIC_FIELDS.has(field)) {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb)) return String(a) === String(b);
    return Math.abs(na - nb) < 0.005;
  }

  if (DATE_FIELDS.has(field)) {
    const ta = new Date(a as string).getTime();
    const tb = new Date(b as string).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return String(a) === String(b);
    return ta === tb;
  }

  if (typeof a === 'boolean' || typeof b === 'boolean') return a === b;

  return String(a) === String(b);
}

/**
 * The columns whose value in `current` differs from `original`, as an update
 * payload. Only `fields` are considered. An empty object means nothing to write.
 */
export function pickChangedBookingFields(
  original: Partial<Booking>,
  current: Partial<Booking>,
  fields: readonly BookingSaveField[] = BOOKING_SAVE_FIELDS,
): BookingUpdate {
  const updates: Record<string, unknown> = {};
  for (const field of fields) {
    if (!bookingFieldEquals(field, original[field], current[field])) {
      updates[field] = current[field] ?? null;
    }
  }
  return updates as BookingUpdate;
}

/** Money columns present in an update payload. */
export function changedMoneyFields(updates: BookingUpdate): BookingMoneyField[] {
  return BOOKING_MONEY_FIELDS.filter((field) => field in updates);
}

/**
 * Money columns whose server value no longer matches the copy loaded at open
 * time. Non-empty means another writer moved the booking's money since load.
 */
export function moneyFieldsMovedSinceLoad(
  loaded: Partial<Booking>,
  server: Partial<Booking>,
): BookingMoneyField[] {
  return BOOKING_MONEY_FIELDS.filter((field) => !bookingFieldEquals(field, loaded[field], server[field]));
}
